import type {
	Apartment,
	ApartmentAlert,
	ApartmentMeters,
} from "@/api/districts/types";
import { Badge } from "@/components/ui/badge";

export function DetailRow({
	label,
	value,
}: {
	label: string;
	value: React.ReactNode;
}) {
	return (
		<div className="flex items-center justify-between gap-4">
			<span className="text-muted-foreground text-sm">{label}</span>
			<span className="text-sm font-medium">{value}</span>
		</div>
	);
}

export function AlertsSection({ alerts }: { alerts: ApartmentAlert[] }) {
	if (alerts.length === 0) return null;
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
				Aktywne alerty
			</span>
			{alerts.map((alert) => (
				<div key={alert.id} className="flex items-center justify-between gap-4">
					<Badge variant="destructive" className="text-xs">
						{alert.alarm}
					</Badge>
					<span className="text-xs text-muted-foreground">
						{new Date(alert.cap_dt).toLocaleString("pl-PL", {
							dateStyle: "short",
							timeStyle: "short",
						})}
					</span>
				</div>
			))}
		</div>
	);
}

export function CurrentMetersSection({
	meters,
	hideElectricity,
}: {
	meters: ApartmentMeters;
	hideElectricity: boolean;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
				Przypisane liczniki
			</span>
			<DetailRow label="Woda zimna" value={meters.cold_water_meter ?? "—"} />
			<DetailRow label="Woda ciepła" value={meters.hot_water_meter ?? "—"} />
			<DetailRow label="Ciepło" value={meters.heat_meter ?? "—"} />
			{!hideElectricity && (
				<DetailRow label="Energia" value={meters.electricity_meter ?? "—"} />
			)}
			{meters.additional_meters.length > 0 && (
				<DetailRow
					label="Dodatkowe"
					value={meters.additional_meters.join(", ")}
				/>
			)}
		</div>
	);
}

export function RatesSection({
	apartment,
	hideElectricity,
}: {
	apartment: Apartment;
	hideElectricity: boolean;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
				Stawki
			</span>
			<DetailRow
				label="Woda zimna"
				value={
					apartment.cold_water_rate != null
						? `${apartment.cold_water_rate} zł/m³`
						: "—"
				}
			/>
			<DetailRow
				label="Woda ciepła"
				value={
					apartment.hot_water_rate != null
						? `${apartment.hot_water_rate} zł/m³`
						: "—"
				}
			/>
			<DetailRow
				label="Ciepło"
				value={
					apartment.heat_rate != null ? `${apartment.heat_rate} zł/GJ` : "—"
				}
			/>
			{!hideElectricity && (
				<DetailRow
					label="Energia"
					value={
						apartment.consumption_rate != null
							? `${apartment.consumption_rate} zł/kWh`
							: "—"
					}
				/>
			)}
		</div>
	);
}
